import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { SERVICES } from '../mock';
import { useBooking } from '../context/BookingContext';

const CATEGORIES = [
  { key: 'all', label: 'All Services' },
  { key: 'aesthetics', label: 'Aesthetics' },
  { key: 'precision', label: 'Precision Health' },
  { key: 'wellness', label: 'Wellness Support' },
];

const ServiceCard = ({ service, index, visible }) => {
  const { openBooking } = useBooking();
  const [hovered, setHovered] = useState(false);
  const isPrecision = service.category === 'precision';
  const isTech = service.category === 'technology';

  return (
    <div
      className={`group relative overflow-hidden bg-white cursor-pointer transition-all duration-700 shadow-sm hover:shadow-lg ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-64">
        <img
          src={service.image}
          alt={service.title}
          className={`w-full h-full object-cover transition-transform duration-700 ${
            hovered ? 'scale-110' : 'scale-100'
          }`}
        />
        <div className={`absolute inset-0 transition-opacity duration-500 ${hovered ? 'opacity-100' : 'opacity-0'} ${isPrecision ? 'bg-stone-900/50' : 'bg-black/40'}`} />
        {/* Hover overlay */}
        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={() => openBooking(service)}
            className="bg-white text-stone-800 text-xs tracking-widest uppercase px-6 py-3 hover:bg-rose-400 hover:text-white transition-colors duration-300"
          >
            Book Now
          </button>
        </div>
        {/* Subtitle Badge */}
        <div className="absolute top-4 left-4 bg-white/90 text-stone-600 text-[10px] tracking-widest uppercase px-3 py-1">
          {service.subtitle}
        </div>
        {/* FDA / tag badge */}
        {service.tag && (
          <div className={`absolute top-4 right-4 text-[9px] tracking-widest uppercase px-2.5 py-1 ${
            isTech ? 'bg-stone-900/80 text-rose-400 border border-rose-400/30' :
            isPrecision ? 'bg-rose-400 text-white' :
            'bg-stone-100/90 text-stone-600'
          }`}>
            {service.tag}
          </div>
        )}
        {/* Badge (level / brand) at bottom */}
        {service.badge && (
          <div className="absolute bottom-4 left-4 bg-rose-400 text-white text-[9px] tracking-widest uppercase px-2.5 py-1">
            {service.badge}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <h3
          className="text-stone-800 text-xl mb-2 leading-snug"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {service.title}
        </h3>
        <p className="text-stone-400 text-sm leading-relaxed mb-4 font-light line-clamp-3">
          {service.description}
        </p>

        {/* Features */}
        <ul className="space-y-1 mb-5">
          {service.features.slice(0, 3).map((feat, i) => (
            <li key={i} className="flex items-center gap-2 text-stone-500 text-xs">
              <div className={`w-1 h-1 rounded-full flex-shrink-0 ${isPrecision ? 'bg-stone-600' : 'bg-rose-400'}`} />
              {feat}
            </li>
          ))}
        </ul>

        {/* Footer */}
        <Link
          to={`/services/${service.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
          className="flex items-center justify-end pt-4 border-t border-stone-100 text-rose-500 hover:text-rose-600 transition-colors group"
        >
          <span className="text-sm font-medium mr-2">Learn more</span>
          <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

// Curated picks shown on the homepage — one or two flagship items per category.
// /services renders the full catalog separately.
const FEATURED_IDS = [1, 4, 3, 6, 10, 5];

const Services = () => {
  const { openBooking } = useBooking();
  const [activeCategory, setActiveCategory] = useState('all');
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const featured = FEATURED_IDS
    .map((id) => SERVICES.find((s) => s.id === id))
    .filter(Boolean);

  const filtered = activeCategory === 'all'
    ? featured
    : SERVICES.filter((s) => s.category === activeCategory).slice(0, 6);

  return (
    <section id="services" ref={ref} className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-rose-400" />
            <span className="text-rose-400 text-xs tracking-[0.4em] uppercase font-light">What We Offer</span>
            <div className="w-8 h-px bg-rose-400" />
          </div>
          <h2
            className="text-stone-800 mb-4"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)' }}
          >
            Our Signature Treatments
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto font-light text-lg leading-relaxed">
            From FDA-cleared device treatments and aesthetic enhancements to concierge-level precision health — everything curated to transform you from the outside in and inside out.
          </p>
        </div>

        {/* Category Filter */}
        <div
          className={`flex flex-wrap justify-center gap-2 mb-14 transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`text-xs tracking-widest uppercase px-5 py-2.5 border transition-all duration-300 ${
                activeCategory === cat.key
                  ? 'bg-rose-400 text-white border-rose-400'
                  : 'border-stone-200 text-stone-500 hover:border-rose-300 hover:text-rose-400'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Service Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} visible={visible} />
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <Link
            to="/services"
            className="group inline-flex items-center gap-2 text-rose-400 hover:text-rose-500 text-xs tracking-[0.3em] uppercase mb-6 transition-colors duration-300"
            data-testid="home-services-view-all"
          >
            View all {SERVICES.length} treatments
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <div>
            <button
              onClick={() => openBooking()}
              className="group inline-flex items-center gap-3 bg-stone-800 hover:bg-rose-400 text-white text-sm tracking-widest uppercase px-12 py-4 transition-all duration-300"
            >
              Book a Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
